/**
 * AuthContext — RCA Tool
 *
 * Auth paths:
 *   A. Email/password → /api/auth (Cloudflare Function, hardcoded users)
 *   B. Google OAuth   → Supabase Auth (supabase.auth.signInWithOAuth)
 *   C. Microsoft OAuth → Supabase Auth (supabase.auth.signInWithOAuth, provider: 'azure')
 *
 * Supabase Auth handles session persistence, token refresh, and OAuth redirects.
 * No MSAL, no Azure SDK, no Google OAuth SDK required.
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react'
import type { Session } from '@supabase/supabase-js'
import { supabase } from '@/lib/supabase'

// ============================================================
// Types
// ============================================================

export type UserRole = 'admin' | 'cio' | 'director' | 'pm' | 'viewer' | 'stakeholder'

export interface AuthUser {
  name: string
  email: string
  role: UserRole
}

export interface AuthContextType {
  user: AuthUser | null
  isAuthenticated: boolean
  isLoading: boolean
  role: UserRole | null
  login: (email: string, password: string) => Promise<void>
  loginWithGoogle: () => Promise<void>
  loginWithMicrosoft: () => Promise<void>
  logout: () => Promise<void>
}

// ============================================================
// Helpers
// ============================================================

const SESSION_KEY = 'rca_auth_session'

function inferRoleFromEmail(email: string): UserRole {
  const lower = email.toLowerCase()
  // Known admins
  if (lower === 'asmorton@gmail.com' || lower === 'andrew@morton-tech.com') return 'admin'
  return 'viewer'
}

function userFromEmail(name: string, email: string): AuthUser {
  return { name: name || email.split('@')[0], email, role: inferRoleFromEmail(email) }
}

// Mirror email to sessionStorage so supabaseService can attach it as a header
function syncSessionStorage(email: string | null) {
  if (email) {
    sessionStorage.setItem('rca_user_email', email)
  } else {
    sessionStorage.removeItem('rca_user_email')
  }
}

// ============================================================
// Context
// ============================================================

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  // ----------------------------------------------------------
  // Bootstrap: check Supabase session + localStorage fallback
  // ----------------------------------------------------------
  useEffect(() => {
    // Supabase session (OAuth users)
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) {
        const email = session.user.email ?? ''
        const name = session.user.user_metadata?.full_name
          ?? session.user.user_metadata?.name
          ?? email
        const authedUser = userFromEmail(name, email)
        setUser(authedUser)
        syncSessionStorage(email)
        setIsLoading(false)
        return
      }

      // Fallback: email/password session stored locally
      try {
        const raw = localStorage.getItem(SESSION_KEY)
        if (raw) {
          const parsed = JSON.parse(raw) as { user: AuthUser; expiresAt: number }
          if (Date.now() < parsed.expiresAt) {
            setUser(parsed.user)
            syncSessionStorage(parsed.user.email)
          } else {
            localStorage.removeItem(SESSION_KEY)
          }
        }
      } catch {
        localStorage.removeItem(SESSION_KEY)
      }

      setIsLoading(false)
    })

    // Listen for Supabase auth state changes (OAuth redirect callback)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session: Session | null) => {
        if (session?.user) {
          const email = session.user.email ?? ''
          const name = session.user.user_metadata?.full_name
            ?? session.user.user_metadata?.name
            ?? email
          const authedUser = userFromEmail(name, email)
          setUser(authedUser)
          syncSessionStorage(email)
        } else {
          // Only clear if no local session exists
          const raw = localStorage.getItem(SESSION_KEY)
          if (!raw) {
            setUser(null)
            syncSessionStorage(null)
          }
        }
      }
    )

    return () => subscription.unsubscribe()
  }, [])

  // ----------------------------------------------------------
  // Path A: Email / Password (Cloudflare Function)
  // ----------------------------------------------------------
  const login = useCallback(async (email: string, password: string): Promise<void> => {
    const res = await fetch('/api/auth', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'login', email, password }),
    })

    const data = await res.json() as {
      success: boolean
      name?: string
      role?: string
      email?: string
      error?: string
      accessToken?: string
      refreshToken?: string
    }

    if (!res.ok || !data.success) throw new Error(data.error ?? 'Invalid credentials.')
    if (!data.email) throw new Error('Incomplete auth response.')

    // Establish a real Supabase session so the JWT is available for API calls
    if (data.accessToken && data.refreshToken) {
      await supabase.auth.setSession({
        access_token: data.accessToken,
        refresh_token: data.refreshToken,
      })
      // onAuthStateChange will fire and set the user — no need to setUser() here
      return
    }

    // Fallback: if Supabase session could not be established, auth fails — do not fall back to email-only
    throw new Error('Authentication failed: could not establish a verified session.')
  }, [])

  // ----------------------------------------------------------
  // Path B: Google OAuth via Supabase
  // ----------------------------------------------------------
  const loginWithGoogle = useCallback(async (): Promise<void> => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: window.location.origin,
        queryParams: { access_type: 'offline', prompt: 'consent' },
      },
    })
    if (error) throw new Error(error.message)
    // Supabase redirects the browser — execution stops here
  }, [])

  // ----------------------------------------------------------
  // Path C: Microsoft OAuth via Supabase
  // ----------------------------------------------------------
  const loginWithMicrosoft = useCallback(async (): Promise<void> => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'azure',
      options: {
        redirectTo: window.location.origin,
        scopes: 'email profile openid',
      },
    })
    if (error) throw new Error(error.message)
    // Supabase redirects the browser — execution stops here
  }, [])

  // ----------------------------------------------------------
  // Logout
  // ----------------------------------------------------------
  const logout = useCallback(async (): Promise<void> => {
    await supabase.auth.signOut()
    localStorage.removeItem(SESSION_KEY)
    syncSessionStorage(null)
    setUser(null)
  }, [])

  const isAuthenticated = user !== null
  const role = user?.role ?? null

  return (
    <AuthContext.Provider value={{
      user, isAuthenticated, isLoading, role,
      login, loginWithGoogle, loginWithMicrosoft, logout,
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextType {
  const ctx = useContext(AuthContext)
  if (ctx === undefined) throw new Error('useAuth must be used within an AuthProvider')
  return ctx
}
